import React, { useState } from "react";
import { ChevronRight, HardDrive } from "lucide-react";
import { cn } from "../../lib/utils";
import { FOLDER_ICON, formatBytes } from "./driveUtils";

function TreeNode({ node, depth, currentId, onSelect, expanded, toggle }) {
  const Icon = FOLDER_ICON[node.kind] || FOLDER_ICON.manual;
  const hasChildren = (node.children || []).length > 0;
  const isOpen = expanded.has(node.id);
  const active = currentId === node.id;
  return (
    <div>
      <div
        data-testid={`sidebar-tree-node-${node.id}`}
        onClick={() => onSelect(node.id)}
        style={{ paddingLeft: 8 + depth * 14 }}
        className={cn(
          "group flex items-center gap-1.5 pr-2 py-1.5 rounded-lg cursor-pointer transition-colors text-sm",
          active ? "bg-surface-hover text-text-primary font-medium" : "text-text-secondary hover:bg-surface-hover hover:text-text-primary"
        )}
      >
        <button
          onClick={(e) => { e.stopPropagation(); if (hasChildren) toggle(node.id); }}
          className={cn("shrink-0 w-4 h-4 flex items-center justify-center rounded transition-transform", !hasChildren && "invisible")}
          data-testid={`sidebar-tree-toggle-${node.id}`}
        >
          <ChevronRight className={cn("w-3.5 h-3.5 transition-transform", isOpen && "rotate-90")} />
        </button>
        <Icon className={cn("w-4 h-4 shrink-0", node.kind === "manual" || node.kind === "root" ? "text-text-secondary" : "text-primary")} />
        <span className="truncate">{node.name}</span>
      </div>
      {isOpen && hasChildren && (
        <div>
          {node.children.map((c) => (
            <TreeNode key={c.id} node={c} depth={depth + 1} currentId={currentId}
              onSelect={onSelect} expanded={expanded} toggle={toggle} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FolderTree({ tree, currentId, onSelect, usedBytes, limitBytes, t, rootId = "root" }) {
  const [expanded, setExpanded] = useState(() => new Set([rootId]));
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const unlimited = limitBytes == null;
  const pct = unlimited ? 8 : Math.min(100, Math.round((usedBytes / Math.max(1, limitBytes)) * 100));
  const near = !unlimited && pct >= 90;

  return (
    <aside className="w-64 shrink-0 border-r bg-surface flex flex-col" data-testid="drive-sidebar">
      <div className="flex-1 overflow-y-auto p-3">
        {tree && (
          <TreeNode node={tree} depth={0} currentId={currentId} onSelect={onSelect}
            expanded={expanded} toggle={toggle} />
        )}
      </div>
      <div className="border-t p-4" data-testid="drive-storage-usage">
        <div className="flex items-center gap-2 text-xs text-text-secondary mb-2">
          <HardDrive className="w-3.5 h-3.5" />
          <span className={cn("font-medium", near ? "text-red-400" : "text-text-primary")}>{formatBytes(usedBytes)}</span>
          {unlimited ? <span>{t("drive_storage_used")}</span> : <span>/ {formatBytes(limitBytes)}</span>}
        </div>
        {!unlimited && (
          <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden">
            <div className={cn("h-full rounded-full transition-all", near ? "bg-red-500" : "bg-primary")} style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </aside>
  );
}
